import { getCookie, setCookie, deleteCookie } from "@/services/cookies";
import { CookieName, CookieRegistry } from "@/services/cookieRegistry";
import { computed, reactive, type WritableComputedRef } from "vue";
import { ItemListLayout } from "@/services/itemList/itemListTypes";

/**
 * Keys for every per-device preference stored in the shared `Preferences` cookie.
 * Mirrors the `IndexedDbKey` pattern in `src/services/indexedDb.ts` — only keys listed here
 * are allowed, so a typo or copy-paste can't silently collide two unrelated preferences.
 */
export enum PreferenceKey {
  BatchBackSideMode = "batchBackSideMode",
  // Legacy — the Master set 'cards'/'rows' toggle now lives under ItemListLayoutCuratedList
  // below. Kept only so migrateLegacyPreferences can fold an existing value into the new key.
  CuratedListDisplayMode = "curatedListDisplayMode",
  RememberedCategory = "rememberedCategory",
  RememberedStrategy = "rememberedStrategy",
  RememberedParameterValues = "rememberedParameterValues",
  LikeHintDismissed = "likeHintDismissed",
  AbsenceHintDismissed = "absenceHintDismissed",
  // Grid/table listing layout, remembered per section (see `useItemListLayout`).
  ItemListLayoutMarketplace = "itemListLayout:marketplace",
  ItemListLayoutDatabase = "itemListLayout:database",
  ItemListLayoutMyItems = "itemListLayout:myItems",
  ItemListLayoutCuratedList = "itemListLayout:curatedList",
}

type PreferenceStore = Partial<Record<PreferenceKey, unknown>>;

const { name: cookieName, expirationDays } = CookieRegistry[CookieName.Preferences];

function readStore(): PreferenceStore {
  const raw = getCookie(cookieName);
  if (raw === null) return {};

  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed !== null && typeof parsed === "object" ? (parsed as PreferenceStore) : {};
  } catch {
    return {};
  }
}

const store = reactive<PreferenceStore>(readStore());

function persist(): void {
  setCookie(cookieName, JSON.stringify(store), {
    expiration: new Date(Date.now() + expirationDays * 24 * 60 * 60 * 1000).toUTCString(),
    path: "/",
  });
}

/**
 * One-time pickup of values from the per-preference cookies/localStorage keys this
 * mechanism replaces, so existing users don't lose their remembered category or see
 * already-dismissed hints reappear. Safe to delete once users have picked this up —
 * the legacy sources are cleared as soon as they're read, and expire on their own besides.
 */
function migrateLegacyPreferences(target: PreferenceStore): boolean {
  let migrated = false;

  if (!(PreferenceKey.LikeHintDismissed in target) && getCookie("like_hint_dismissed") !== null) {
    target[PreferenceKey.LikeHintDismissed] = true;
    deleteCookie("like_hint_dismissed");
    migrated = true;
  }

  if (!(PreferenceKey.AbsenceHintDismissed in target) && getCookie("absence_hint_dismissed") !== null) {
    target[PreferenceKey.AbsenceHintDismissed] = true;
    deleteCookie("absence_hint_dismissed");
    migrated = true;
  }

  if (!(PreferenceKey.RememberedCategory in target)) {
    const raw = getCookie("colectio_remembered_category");
    if (raw !== null) {
      target[PreferenceKey.RememberedCategory] = Number(raw);
      deleteCookie("colectio_remembered_category");
      migrated = true;
    }
  }

  if (!(PreferenceKey.BatchBackSideMode in target)) {
    const raw = localStorage.getItem("colectio.batch.backSideMode");
    if (raw !== null) {
      try {
        target[PreferenceKey.BatchBackSideMode] = JSON.parse(raw);
        migrated = true;
      } catch {
        // Malformed legacy value — nothing worth carrying over.
      }
      localStorage.removeItem("colectio.batch.backSideMode");
    }
  }

  if (!(PreferenceKey.CuratedListDisplayMode in target)) {
    const raw = localStorage.getItem("curated-list-display-mode");
    if (raw !== null) {
      target[PreferenceKey.CuratedListDisplayMode] = raw;
      localStorage.removeItem("curated-list-display-mode");
      migrated = true;
    }
  }

  // Master set toggle unified onto the shared ItemListLayout vocabulary — fold any existing
  // 'cards'/'rows' value into the new key, then drop the legacy one.
  if (!(PreferenceKey.ItemListLayoutCuratedList in target) && PreferenceKey.CuratedListDisplayMode in target) {
    target[PreferenceKey.ItemListLayoutCuratedList] = target[PreferenceKey.CuratedListDisplayMode] === "rows" ? ItemListLayout.Table : ItemListLayout.Grid;
    delete target[PreferenceKey.CuratedListDisplayMode];
    migrated = true;
  }

  return migrated;
}

if (migrateLegacyPreferences(store)) persist();

// Every preference shares one computed instance per key, so unrelated call sites
// for the same preference (e.g. remembered category, read from many views) stay in sync.
const cache = new Map<PreferenceKey, WritableComputedRef<unknown>>();

/**
 * Reactive per-device preference backed by the shared `Preferences` cookie.
 *
 * `validate` lets a caller narrow/reject an untrusted stored value; it defaults to trusting
 * the parsed JSON as-is, which is enough for plain strings, numbers and booleans.
 */
export function usePreference<T>(key: PreferenceKey, defaultValue: T, validate: (raw: unknown) => T = (raw) => raw as T): WritableComputedRef<T> {
  const cached = cache.get(key);
  if (cached) return cached as WritableComputedRef<T>;

  const preference = computed<T>({
    get: () => (key in store ? validate(store[key]) : defaultValue),
    set: (value) => {
      store[key] = value;
      persist();
    },
  });

  cache.set(key, preference as WritableComputedRef<unknown>);
  return preference;
}
